import {
  css, type Interpolation,
} from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  type CSSProperties,
  type ReactNode, useCallback,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import {
  type TooltipBehavior, TooltipComponent, TooltipPlacement,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import { type Theme } from '~/_shared/themes/theme.model';
import { OverlayLoaderComponent } from '../../../components/overlay/overlayLoader.component';

export const SQUARE_ICON_BUTTON_HOVER_SHADOW_SPREAD = 2;
const defaultBorderRadius = 8;

const activeStyles = (theme: Theme) => css({
  backgroundColor: theme.backgroundColors.quaternary,
  color: theme.iconColors.quaternary,

  '&:active': {
    backgroundColor: theme.backgroundColors.quaternary,
    color: theme.iconColors.quaternary,
  },

  '&:hover': {
    backgroundColor: theme.backgroundColors.quaternary,
    color: theme.iconColors.quaternary,
  },
});

const hoverStyles = (theme: Theme) => css({
  '&:hover': {
    backgroundColor: theme.iconColors.backgroundQuaternaryHover,
    border: `2px solid ${theme.borderColors.activeItem}`,
    boxShadow: `inset ${theme.backgroundColors.secondary} 0px 0px 6px ${SQUARE_ICON_BUTTON_HOVER_SHADOW_SPREAD}px`,
    color: theme.iconColors.quaternary,
  },
});

const rootStyle = (theme: Theme) => css({
  backgroundColor: theme.backgroundColors.quinary,
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: `0 0 0 1px ${theme.borderColors.primary}`,
  boxSizing: 'border-box',
  color: theme.iconColors.highContrast,
  fontWeight: 900,
});

const loaderStyle = ({ borderRadius, isActive }: {
  borderRadius?: CSSProperties['borderRadius'];
  isActive?: boolean;
}) => (theme: Theme) => css({
  borderRadius: borderRadius ?? defaultBorderRadius,
  ...(isActive ? {
    boxShadow: `0 0 0 1px ${theme.borderColors.primary}`,
    top: -2,
    left: -2,
    boxSizing: 'border-box',
    width: 'calc(100% + 4px)',
    height: 'calc(100% + 4px)',
  } : undefined),
});

export type SquareIconButtonProps = Readonly<{
  className?: string;
  icon: IconProp;
  isActive?: boolean;
  tooltip?: string;
  tooltipContentStyle?: CSSInterpolation;
  tooltipPlacement?: TooltipPlacement;
  tooltipOffset?: number;
  tooltipBehavior?: TooltipBehavior;
  buttonSize?: number;
  useDefaultHoverStyles?: boolean;
  borderRadius?: CSSProperties['borderRadius'];
  isDisabled?: boolean;
  isLoading?: boolean;

  onClick: () => void;
  renderOverlay?: (css: Interpolation) => ReactNode;
}>;

const defaultOverlayRenderer: SquareIconButtonProps['renderOverlay'] = (style) => (
  <OverlayLoaderComponent
    css={style}
    loaderSize={21}
  />
);

export const SquareIconButtonComponent = ({
  borderRadius = defaultBorderRadius,
  buttonSize = 32,
  className,
  icon,
  isActive = false,
  isDisabled,
  isLoading,
  onClick,
  renderOverlay = defaultOverlayRenderer,
  tooltip,
  tooltipBehavior,
  tooltipContentStyle,
  tooltipOffset,
  tooltipPlacement = TooltipPlacement.Left,
  useDefaultHoverStyles,
  ...restProps
}: SquareIconButtonProps) => {
  const handleClick = useCallback(() => {
    if (!isDisabled) {
      onClick();
    }
  }, [isDisabled, onClick]);

  return (
    <TooltipComponent
      behavior={tooltipBehavior}
      tooltipContent={tooltip}
      placement={tooltipPlacement}
      contentStyle={tooltipContentStyle}
      offset={tooltipOffset}
    >
      <div
        className={className}
        style={{
          height: buttonSize,
          width: buttonSize,
          borderRadius,
        }}
        css={[
          rootStyle,
          (!isDisabled && !isActive && useDefaultHoverStyles) ? hoverStyles : undefined,
          isActive ? activeStyles : undefined,
        ]}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        {...restProps}
      >
        {isLoading && renderOverlay(loaderStyle({ borderRadius, isActive }))}
        <FontAwesomeIcon icon={icon} />
      </div>
    </TooltipComponent>
  );
};
